import React, { useState } from "react";

/**
 * ShareMatchModal
 * Lets users share a match link via WhatsApp, SMS, clipboard, or any
 * Web Share API-capable app (Instagram, Telegram, etc.).
 *
 * Props:
 *   isOpen   – boolean
 *   onClose  – function
 *   matchId  – string | number (match id / short_code)
 *   matchTitle – string (display name for the share message)
 */
const ShareMatchModal = ({ isOpen, onClose, matchId, matchTitle }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const shareUrl = `${window.location.origin}/matches/${matchId}`;
  const shareText = matchTitle
    ? `Check out this match on Sportifide: ${matchTitle}`
    : "Check out this match on Sportifide!";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback for browsers without clipboard API
      const input = document.createElement("input");
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleSMS = () => {
    window.open(
      `sms:?body=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: matchTitle || "Sportifide Match", text: shareText, url: shareUrl });
      } catch {
        // user cancelled — no-op
      }
    }
  };

  return (
    <div
      className="modal-overlay share-match-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="share-match-modal">
        <div className="share-match-header">
          <h3 className="share-match-title">🔗 Share Match</h3>
          <button className="share-match-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <p className="share-match-desc">
          Share this match with friends. Anyone who clicks the link will be
          asked to sign in and taken straight to the match page.
        </p>

        {/* Link preview */}
        <div className="share-match-link-row">
          <span className="share-match-link-text">{shareUrl}</span>
          <button
            className={`share-match-copy-btn ${copied ? "copied" : ""}`}
            onClick={handleCopy}
          >
            {copied ? "✅ Copied!" : "📋 Copy"}
          </button>
        </div>

        {/* Share targets */}
        <div className="share-match-targets">
          <button className="share-target-btn share-target-whatsapp" onClick={handleWhatsApp}>
            <span className="share-target-icon">💬</span>
            <span>WhatsApp</span>
          </button>

          <button className="share-target-btn share-target-sms" onClick={handleSMS}>
            <span className="share-target-icon">📱</span>
            <span>SMS</span>
          </button>

          {navigator.share && (
            <button className="share-target-btn share-target-more" onClick={handleNativeShare}>
              <span className="share-target-icon">⬆️</span>
              <span>More</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareMatchModal;
